import { Response } from 'miragejs'
import { randCompanyName } from '@ngneat/falso'
import type { MockServer } from './server.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'

// In-memory per-patient registration state for mock server
type PatientRegistrationState = {
  isRegistered: boolean
  error: boolean
  vetenvoyResult: string | null
  receiptUrl: string | null
  title: string | null
  errorMessage: string | null
  acknowledged: boolean
  // Mirrors VetEnvoyMicrochip.create_notification — set once per registration cycle
  notificationCreated: boolean
}

const patientStates = new Map<string, PatientRegistrationState>()

function getPatientState(patientId: string): PatientRegistrationState {
  if (!patientStates.has(patientId)) {
    patientStates.set(patientId, {
      isRegistered: false,
      error: false,
      vetenvoyResult: null,
      receiptUrl: null,
      title: null,
      errorMessage: null,
      acknowledged: true,
      notificationCreated: false,
    })
  }
  return patientStates.get(patientId)!
}

export function routes(server: MockServer) {
  // GET /vetenvoy/microchip/patient/:id/status
  server.get('/vetenvoy/microchip/patient/:id/status', (_schema, request) => {
    const patientId = request.params.id
    const state = getPatientState(patientId)

    return {
      data: {
        type: 'VetEnvoyPatientMicrochipStatus',
        id: patientId,
        attributes: {
          id: Number(patientId),
          is_registered: state.isRegistered,
          error: state.error,
          vetenvoy_result: state.vetenvoyResult,
          receipt_url: state.receiptUrl,
          title: state.title,
          error_message: state.errorMessage,
          acknowledged: state.acknowledged,
          // Channel is only present while a registration is in progress
          websocket_channel:
            state.vetenvoyResult && !state.isRegistered && !state.error
              ? `vetenvoy.microchip.patient.${patientId}`
              : undefined,
        },
      },
    }
  })

  // GET /vetenvoy/microchip/patient/:id/validate
  server.get('/vetenvoy/microchip/patient/:id/validate', (_schema, request) => {
    const patientId = request.params.id
    const hasErrors = patientId === '99'

    return {
      data: {
        type: 'VetEnvoyPatientMicrochipValidation',
        id: patientId,
        attributes: {
          id: Number(patientId),
          valid: !hasErrors,
          required: hasErrors ? ['client_phone', 'patient_name'] : [],
        },
      },
    }
  })

  // POST /vetenvoy/microchip/patient/:id/register
  server.post(
    '/vetenvoy/microchip/patient/:id/register',
    (_schema, request) => {
      const patientId = request.params.id
      const state = getPatientState(patientId)

      // Block registration if previous result is unacknowledged (matches backend behavior)
      if (!state.acknowledged) {
        return new Response(
          400,
          {},
          {
            errors: [
              {
                detail:
                  'Registration result has not been presented to the user yet.',
                status: '400',
                source: { pointer: '/data' },
              },
            ],
          },
        )
      }

      // Pending state — result arrives via WS; notification created in
      // on_microchip_register_complete, not here.
      state.isRegistered = false
      state.error = false
      state.vetenvoyResult = 'STATUS_STARTED'
      state.receiptUrl = null
      state.title = null
      state.errorMessage = null
      state.acknowledged = false
      state.notificationCreated = false

      return new Response(202)
    },
  )

  // POST /vetenvoy/microchip/patient/:id/trigger (mock-only)
  // Simulates on_microchip_register_complete() — updates state + creates notification.
  server.post('/vetenvoy/microchip/patient/:id/trigger', (schema, request) => {
    const patientId = request.params.id
    const provetId = request.params.provet_id
    const state = getPatientState(patientId)
    const body = JSON.parse(request.requestBody) as { isSuccess: boolean }

    if (body.isSuccess) {
      state.isRegistered = true
      state.error = false
      state.vetenvoyResult = 'ACCEPTED'
      state.receiptUrl = '#'
      state.title =
        'VetEnvoy: microchip for Milo (345) successfully registered!'
      state.errorMessage = null
    } else {
      state.isRegistered = false
      state.error = true
      state.vetenvoyResult = 'INVALID_DATA'
      state.receiptUrl = null
      state.title = 'VetEnvoy microchip number for Milo (345) failed!'
      state.errorMessage =
        'Something went wrong, please contact Support or try again later.'
    }

    // One notification per registration cycle — notificationCreated is a one-way flag.
    if (state.notificationCreated) {
      return new Response(204)
    }
    state.notificationCreated = true

    schema.create('notification', {
      role: 'target',
      action_type: 'updated',
      sub_type: body.isSuccess ? 'status-success' : 'status-error',
      content_type_name: 'vet envoy microchip',
      message_content: body.isSuccess
        ? 'VetEnvoy: microchip for Milo (345) successfully registered!'
        : 'VetEnvoy microchip number for Milo (345) failed!',
      message_error: body.isSuccess
        ? ''
        : 'Something went wrong, please contact Support or try again later.',
      message_subject: '',
      legacy_url: `/provet/${provetId}/client/1/patient/?patient_id=${patientId}`,
      created: getTodayDateTimeISO(),
      read: null,
      recipient: schema.first('user'),
    })

    return new Response(204)
  })

  // POST /vetenvoy/microchip/patient/:id/acknowledge
  server.post(
    '/vetenvoy/microchip/patient/:id/acknowledge',
    (_schema, request) => {
      const patientId = request.params.id
      const state = getPatientState(patientId)
      state.acknowledged = true
      state.notificationCreated = false

      return new Response(204)
    },
  )

  // GET /vetenvoy/microchip/providers
  server.get('/vetenvoy/microchip/providers', () => {
    return {
      data: [
        {
          type: 'VetEnvoyMicrochipProvider',
          id: '1',
          attributes: {
            provider_name: randCompanyName(),
          },
        },
        {
          type: 'VetEnvoyMicrochipProvider',
          id: '2',
          attributes: {
            provider_name: randCompanyName(),
          },
        },
      ],
      meta: {
        pagination: { count: 2, pages: 1, page: 1 },
      },
    }
  })

  // GET /vetenvoy/microchip/providers/:id
  server.get('/vetenvoy/microchip/providers/:id', (_schema, request) => {
    return {
      data: {
        type: 'VetEnvoyMicrochipProvider',
        id: request.params.id,
        attributes: {
          provider_name: randCompanyName(),
        },
      },
    }
  })
}
